import React from "react"
import { type To } from "react-router"
import { css, type Theme, SerializedStyles } from "@emotion/react"
import {
  autoUpdate,
  FloatingFocusManager,
  FloatingList,
  offset,
  type Placement,
  type MiddlewareState,
  size,
  useFloating,
  useListItem,
  flip,
  shift,
} from "@floating-ui/react"
import { useActionListRoot } from "@myvp/shared/src/components/action-list/action-list-root"
import useMergeRefs from "@myvp/shared/src/hooks/use-merge-refs"
import {
  type IconColor,
  type IconElement,
} from "@myvp/shared/src/styles/icon-utils"
import { Medium14, Regular12UI, Overline12 } from "@myvp/shared/src/typography"
import { FloatingPortal } from "@myvp/shared/src/components/floating/portal"
import {
  // oxlint-disable-next-line no-restricted-imports
  InternalLink,
  InternalAnchor,
} from "@myvp/shared/src/components/buttons/button-utils"
import Checkbox from "@myvp/shared/src/components/checkbox"
import Tooltip, {
  TooltipProps,
} from "@myvp/shared/src/components/tooltip/tooltip"

export interface SharedActionListItemData {
  variant?: "default" | "destructive"
  startIcon?: IconElement
  label: React.ReactNode
  subLabel?: React.ReactNode
  suffixContent?: React.ReactNode
  disabled?: boolean
  // This is being used but for some reason being reported
  // oxlint-disable-next-line eslint-react/no-unused-prop-types
  style?: SerializedStyles | ((theme: Theme) => SerializedStyles) | null
  // This is being used but for some reason being reported
  // oxlint-disable-next-line eslint-react/no-unused-prop-types
  tooltipProps?: Omit<TooltipProps, "children"> & { css?: SerializedStyles }
}
export interface ActionListButtonData extends SharedActionListItemData {
  type?: "item"
  onClick: () => void
}
export interface ActionListLinkData extends SharedActionListItemData {
  type: "link"
  to: To
  replace?: boolean
  state?: unknown
  target?: React.HTMLAttributeAnchorTarget
  rel?: string
}
type Checkbox = SharedActionListItemData &
  Omit<React.ComponentProps<typeof Checkbox>, "type">
export interface ActionListCheckboxData extends Checkbox {
  type: "checkbox"
}

type Anchor = SharedActionListItemData & React.ComponentProps<"a">
export interface ActionListAnchorData extends Anchor {
  type: "anchor"
}

export interface ActionListGroupData {
  type: "group"
  label: React.ReactNode
  actions: ActionListItems[]
}
export type ActionListItems =
  | ActionListButtonData
  | ActionListLinkData
  | ActionListAnchorData
  | ActionListCheckboxData
  | undefined
  | null
  | false
export type ActionListActions = (ActionListItems | ActionListGroupData)[]

const popoverCss = (theme: Theme) => css`
  position: absolute;
  border-radius: 4px;
  color: ${theme.palette.content.neutral.default};
  background: ${theme.palette.surface.neutral.default};
  box-shadow: 0px 12px 48px 0px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  z-index: 1000;
`
const listContainerCss = css`
  list-style: none;
  padding: 4px;
  margin: 0;
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  overflow-wrap: anywhere;
`

const ActionList = (props: {
  className?: string
  actions?: ActionListActions
  placement?: Placement
  ref?: React.RefObject<HTMLDivElement>
  applySize?: (
    args: MiddlewareState & {
      availableWidth: number
      availableHeight: number
    }
  ) => Partial<CSSStyleDeclaration>
  preventFlip?: boolean
  preventShift?: boolean
}) => {
  const actionListRoot = useActionListRoot()
  const { floatingStyles, context } = useFloating({
    rootContext: actionListRoot.rootContext,
    placement: props.placement ?? "bottom",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      !props.preventFlip && flip({ padding: 8 }),
      !props.preventShift && shift({ padding: 8 }),
      size({
        padding: {
          top: 16,
          bottom: 16,
        },
        apply: (args) => {
          Object.assign(args.elements.floating.style, {
            maxHeight: `${args.availableHeight}px`,
            minWidth: `${args.rects.reference.width}px`,
            maxWidth: `${args.availableWidth}px`,
            ...props.applySize?.(args),
          })
        },
      }),
    ],
  })
  const ref = useMergeRefs([actionListRoot.actionListRef, props.ref])

  if (!actionListRoot.rootContext.open) {
    return null
  }
  const ui = (
    <div
      className={props.className}
      ref={ref}
      css={popoverCss}
      {...actionListRoot.getFloatingProps({})}
      style={floatingStyles}
    >
      <ul role="menu" css={listContainerCss}>
        <List
          actions={props.actions}
          onDismiss={() => {
            actionListRoot.rootContext.onOpenChange(false)
          }}
        />
      </ul>
    </div>
  )
  return (
    <FloatingPortal>
      <FloatingList elementsRef={actionListRoot.listRef}>
        <FloatingFocusManager context={context} modal={false}>
          {ui}
        </FloatingFocusManager>
      </FloatingList>
    </FloatingPortal>
  )
}

const List = (props: {
  actions?: (ActionListItems | ActionListGroupData)[]
  onDismiss: () => void
}) => {
  return (
    <>
      {props.actions?.map((action, index) => {
        if (!action) return null
        if (action.type === "group") {
          if (action.actions.every((action) => !action)) {
            return null
          }
          return (
            <ActionListGroup key={index} label={action.label}>
              <List actions={action.actions} onDismiss={props.onDismiss} />
            </ActionListGroup>
          )
        }
        const children = (() => {
          switch (action.type) {
            case "anchor":
              return <ActionListAnchor key={index} {...action} />
            case "link":
              return <ActionListLink key={index} {...action} />
            case "checkbox":
              return <ActionListCheckbox key={index} {...action} />
            case "item":
            default:
              return (
                <ActionListItem
                  key={index}
                  {...action}
                  onDismiss={props.onDismiss}
                />
              )
          }
        })()
        return action.tooltipProps ? (
          <Tooltip {...action.tooltipProps} key={index}>
            <div>{children}</div>
          </Tooltip>
        ) : (
          children
        )
      })}
    </>
  )
}

export default ActionList

const listItemCss = (theme: Theme) => css`
  display: flex;
  // MYVC-45747: old versions of chrome set 'align-items: flex-start' on buttons.
  align-items: initial;
  width: 100%;
  min-height: 36px;
  padding: 1px 4px;
  border-radius: 4px;

  :not(:disabled, &[aria-disabled="true"]) {
    :focus-visible {
      outline: 2px solid ${theme.palette.border.focus.default};
    }
    :hover {
      background: ${theme.palette.surface.neutral.hover};
    }
  }
  :disabled,
  &[aria-disabled="true"] {
    cursor: not-allowed;
  }
`
const textAltCss = (theme: Theme) => css`
  color: ${theme.palette.content.neutral.subtle};
`
const textDestructiveCss = (theme: Theme) => css`
  color: ${theme.palette.content.critical.default};
`
const textDisabledCss = (theme: Theme) => css`
  color: ${theme.palette.content.neutral.disabled};
`
const textDisabledAltCss = (theme: Theme) => css`
  color: ${theme.palette.content.neutral.disabled};
`
const ActionListItemContent = function ActionListItemContent(
  props: SharedActionListItemData
) {
  let iconColor: IconColor
  if (props.disabled) {
    iconColor = "disabled"
  } else if (props.variant === "destructive") {
    iconColor = "critical"
  } else {
    iconColor = "default"
  }
  return (
    <div
      css={[
        props.variant === "destructive" && textDestructiveCss,
        props.disabled && textDisabledAltCss,
        css`
          display: flex;
          align-items: center;
          column-gap: 4px;
          flex: 1 0 0;
          text-align: start;
        `,
      ]}
    >
      {props.startIcon?.({
        color: iconColor,
        size: "medium",
      }) ?? null}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          flex: 1 0 0;
        `}
      >
        <Medium14 as="div">{props.label}</Medium14>
        {props.subLabel && (
          <Regular12UI
            as="div"
            css={[textAltCss, props.disabled && textDisabledCss]}
          >
            {props.subLabel}
          </Regular12UI>
        )}
      </div>
      {props.suffixContent}
    </div>
  )
}

const buttonResetCss = css`
  // allow styling border radius in ios safari
  appearance: button;

  // inherit font values in all browsers
  font: inherit;
  font-feature-settings: inherit;
  font-variation-settings: inherit;
  letter-spacing: inherit;
  color: inherit;

  background-color: transparent;
  border-width: 0;

  cursor: pointer;
`
const ActionListItem = function ActionListItem(
  props: ActionListButtonData & { onDismiss: () => void }
) {
  const { ref } = useListItem()
  return (
    <li role="menuitem">
      <button
        ref={ref}
        css={[buttonResetCss, listItemCss, props.style]}
        disabled={props.disabled}
        onClick={() => {
          props.onDismiss()
          props.onClick()
        }}
      >
        <ActionListItemContent {...props} />
      </button>
    </li>
  )
}

const linkResetCss = css`
  text-decoration: none;

  // inherit font values in all browsers
  font: inherit;
  font-feature-settings: inherit;
  font-variation-settings: inherit;
  letter-spacing: inherit;
  color: inherit;

  background-color: transparent;

  cursor: pointer;
`
const ActionListLink = function ActionListLink(props: ActionListLinkData) {
  const { ref } = useListItem()
  return (
    <li role="menuitem">
      <InternalLink
        ref={ref}
        css={[linkResetCss, listItemCss, props.style]}
        aria-disabled={props.disabled}
        to={props.to}
        replace={props.replace}
        state={props.state}
        target={props.target}
        rel={props.rel}
      >
        <ActionListItemContent {...props} />
      </InternalLink>
    </li>
  )
}

const ActionListAnchor = function ActionListAnchor(
  props: ActionListAnchorData
) {
  const { ref } = useListItem()
  return (
    <li role="menuitem">
      <InternalAnchor
        ref={ref}
        css={[linkResetCss, listItemCss, props.style]}
        aria-disabled={props.disabled}
        {...props}
      >
        <ActionListItemContent {...props} />
      </InternalAnchor>
    </li>
  )
}

const checkboxResetCss = (theme: Theme) => css`
  background-color: ${theme.palette.surface.neutral.default};
  :hover {
    background-color: ${theme.palette.surface.neutral.hover};
  }
  :focus-visible {
    outline: 1px solid ${theme.palette.content.link.default};
  }
  padding: 8px;
  min-height: 40px;
  height: 40px;
  svg {
    min-height: 16px;
    min-width: 16px;
    height: 16px;
    width: 16px;
    margin-inline-end: 8px;
  }
`

const ActionListCheckbox = function ActionListCheckbox(
  props: ActionListCheckboxData
) {
  const { type: _type, label, style, ...restProps } = props
  return (
    <li role="menuitem">
      <Checkbox
        css={[checkboxResetCss, style]}
        type="button"
        {...restProps}
        variant="button"
        label={(() => {
          if (typeof label === "string") {
            return <Medium14>{label}</Medium14>
          } else {
            return label
          }
        })()}
      />
    </li>
  )
}

const groupCss = (theme: Theme) => css`
  :not(:first-of-type) {
    padding-top: 6px;
    border-top: 1px solid ${theme.palette.border.neutral.subtle};
  }
`
const groupHeaderCss = css`
  padding-block: 6px 8px;
  padding-inline: 4px;
  text-align: start;
`
const groupListCss = css`
  list-style: none;
  padding: 0px;
  margin: 0;
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`
const ActionListGroup = function ActionListGroup(
  props: Omit<ActionListGroupData, "actions" | "type"> & {
    children?: React.ReactNode
  }
) {
  const htmlId = React.useId()
  const labelId = `group-label-${htmlId}`
  return (
    <li role="none" css={groupCss}>
      <div role="none" css={groupHeaderCss}>
        {typeof props.label === "string" ? (
          <Overline12
            as="p"
            id={labelId}
            css={(theme) => css`
              color: ${theme.palette.content.neutral.subtle};
            `}
          >
            {props.label.toLocaleUpperCase()}
          </Overline12>
        ) : (
          props.label
        )}
      </div>
      <ul role="group" css={groupListCss} aria-labelledby={labelId}>
        {props.children}
      </ul>
    </li>
  )
}
